import React, { useState, useEffect } from 'react';
import { Container, TextField, Typography, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { Button, IdentityForm, LineItem, PasswordValidationWidget } from '../../../components';
import { Link, useNavigate } from 'react-router-dom';
import { checkCampaign, registerAccount } from '../../../api';
import { useAlerts } from '../../../alerts';
import { GradientLogo } from '@wave-engagement/wave-react';
import { MuiTelInput } from 'mui-tel-input';
const WELCOME_VIDEO_URL = 'https://wave-public.s3.us-east-2.amazonaws.com/videos/Emily+Wave+Engagement+Vert+smaller.mov';

const cleanup = (value) => value?.trim() === '' ? undefined : value;

const CreateAccount = () => {
  const navigate = useNavigate();
  const { setSuccess, setError, clearErrors } = useAlerts();
  const [pageIndex, setPageIndex] = useState(0);
  const [campaignCode, setCampaignCode] = useState('');
  const [isLoadingCheckOrganizationCode, setIsLoadingCheckOrganizationCode] = useState(false);
  const [campaign, setCampaign] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [screenName, setScreenName] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [notifyEmail, setNotifyEmail] = useState(false);
  const [notifyPhone, setNotifyPhone] = useState(false);
  const [notifyPush, setNotifyPush] = useState(false);
  const [agreeToTerms, setAgreeToTerms] = useState(false);
  const [demographics, setDemographics] = useState(undefined);

  useEffect(() => {
    return () => clearErrors();
  }, []);

  const onCheckOrganizationCode = (e) => {
    e.preventDefault();

    setIsLoadingCheckOrganizationCode(true);

    // Check that the organization code is valid
    checkCampaign(campaignCode).then(res => {
      setCampaign(res);
      setPageIndex(2);
      clearErrors();
    }).catch(error => {
      setError(error ?? 'An error occurred while trying to check the organization code.');
    }).finally(() => {
      setIsLoadingCheckOrganizationCode(false);
    });
  }

  const onSubmit = (e) => {
    e.preventDefault();

    if (screenName.length < 3) {
      setError('Screen name must be at least 3 characters long');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (!agreeToTerms) {
      setError('You must agree to the terms and conditions');
      return;
    }

    let demographicsCopy = {};
    if (demographics != null) {
      const {
        caregivingStatus,
        ethnicity,
        race,
        gender,
        otherGender,
        sexualOrientation,
        generation,
        location,
        disability,
        languageAccent,
        relationshipStatus,
        militaryAffiliation,
        religiousAffiliation,
        supervisoryStatus,
        tenure,
        unionAffiliation,
        workLocation,
        openIdentity
      } = demographics;
      demographicsCopy = {
        caregivingStatus: cleanup(caregivingStatus),
        ethnicity: cleanup(ethnicity),
        race: cleanup(race),
        gender: `${gender}${otherGender != null && cleanup(otherGender) !== '' ? `;;;${cleanup(otherGender)}` : ''}`,
        sexualOrientation: cleanup(sexualOrientation),
        generation: cleanup(generation),
        location: cleanup(location),
        disability: cleanup(disability),
        languageAccent: cleanup(languageAccent),
        relationshipStatus: cleanup(relationshipStatus),
        militaryAffiliation: cleanup(militaryAffiliation),
        religiousAffiliation: cleanup(religiousAffiliation),
        supervisoryStatus: cleanup(supervisoryStatus),
        tenure: cleanup(tenure),
        unionAffiliation: cleanup(unionAffiliation),
        workLocation: cleanup(workLocation),
        openIdentity: cleanup(openIdentity),
      };
    }

    setIsLoading(true);
    registerAccount({
      code: campaignCode,
      firstName,
      lastName,
      email,
      phoneNumber: phoneNumber !== '' ? phoneNumber : undefined,
      notifyEmail,
      notifyPhone,
      notifyPush,
      screenName,
      password,
      ...demographicsCopy
    }).then(() => {
      setSuccess('Successfully registered. Please check your email to verify your account.');
      navigate('/auth/login');
    }).catch(error => {
      setError(error ?? 'Unable to register. Please try again.');
    }).then(() => {
      setIsLoading(false);
    });
  }

  return (
    <Container>
      {/* Video Explainer */}
      {pageIndex === 0 &&
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <video
          controls
          style={{ borderRadius: '8px', maxWidth: '90vw', maxHeight: '90vh' }}
        >
          <source src={WELCOME_VIDEO_URL} type="video/mp4" />
          Your browser does not support the video tag.
        </video>
        <Button fullWidth onClick={() => setPageIndex(1)} style={{ marginTop: '10px' }}>Create Account</Button>
      </div>}

      {/* Campaign Code */}
      {pageIndex === 1 &&
      <form onSubmit={onCheckOrganizationCode}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '3em' }}>
          <img src={GradientLogo} alt='Wave Engagement Logo' style={{ width: '100%', maxWidth: '300px', objectFit: 'contain', marginTop: '3em' }} />
        </div>
        <Typography variant='h1' gutterBottom>Create Account</Typography>
        <TextField
          required
          label='Your Organization Code'
          onChange={(e) => setCampaignCode(e.target.value)}
          disabled={isLoadingCheckOrganizationCode}
          variant='filled'
          style={{
            width: '100%',
            marginBottom: '0.5em'
          }}
        />
        <Typography variant='button' style={{ marginBottom: '10px' }}>CONTACT YOUR ADMINISTRATOR IF UNKNOWN</Typography>
        <Button fullWidth type='submit' isLoading={isLoadingCheckOrganizationCode}>Next</Button>
        <Button fullWidth secondary onClick={() => setPageIndex(0)} disabled={isLoadingCheckOrganizationCode} style={{ marginTop: '10px' }}>Back</Button>
        <Link to='/auth/login' style={{ textDecoration: 'none' }} disabled={isLoadingCheckOrganizationCode}>Already have an account?</Link>
      </form>}

      {/* Account Info */}
      {pageIndex === 2 &&
      <form onSubmit={onSubmit}>
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '3em' }}>
          <img src={GradientLogo} alt='Wave Engagement Logo' style={{ width: '100%', maxWidth: '300px', objectFit: 'contain', marginTop: '3em' }} />
        </div>
        <Typography variant='h1' gutterBottom>Create Account</Typography>

        <LineItem
          value={campaign?.getOrganizationName()}
          description='Organization'
        />

        {/* First Name */}
        <TextField
          required
          label='First Name'
          onChange={(e) => setFirstName(e.target.value)}
          disabled={isLoading}
          variant='filled'
          style={{
            width: '100%',
            marginBottom: '0.5em'
          }}>
        </TextField>

        {/* Last Name */}
        <TextField
          required
          label='Last Name'
          onChange={(e) => setLastName(e.target.value)}
          disabled={isLoading}
          variant='filled'
          style={{
            width: '100%',
            marginBottom: '0.5em'
          }}>
        </TextField>

        {/* Email */}
        <TextField
          required
          label='Email Address'
          type='email'
          onChange={(e) => setEmail(e.target.value)}
          disabled={isLoading}
          variant='filled'
          style={{
            width: '100%',
            marginBottom: '0.5em'
          }}>
        </TextField>

        {/* Phone Number */}
        <MuiTelInput
          value={phoneNumber}
          onChange={setPhoneNumber}
          defaultCountry='US'
          variant='filled'
          label='Phone Number'
          disabled={isLoading}
          style={{
            width: '100%',
            marginBottom: '0.5em'
          }}
        />

        {/* Screen Name */}
        <TextField
          required
          label='Screen Name'
          onChange={(e) => setScreenName(e.target.value)}
          disabled={isLoading}
          variant='filled'
          style={{
            width: '100%',
            marginBottom: '0.5em'
          }}>
        </TextField>

        {/* Password */}
        <TextField
          required
          label='Password'
          type='password'
          onChange={(e) => setPassword(e.target.value)}
          disabled={isLoading}
          variant='filled'
          style={{
            width: '100%',
            marginBottom: '0.5em'
          }}>
        </TextField>

        <PasswordValidationWidget password={password} />

        {/* Confirm Password */}
        <TextField
          required
          label='Confirm Password'
          type='password'
          onChange={(e) => setConfirmPassword(e.target.value)}
          disabled={isLoading}
          variant='filled'
          style={{
            width: '100%',
            marginBottom: '0.5em',
            marginTop: '0.5em'
          }}>
        </TextField>

        {/* Notifications */}
        <Typography variant='h2' style={{ marginTop: '1em' }}>Notifications</Typography>
        <Typography variant='body1'>You can change these settings at any time from your profile.</Typography>
        <FormGroup style={{ width: '300px' }}>
          <FormControlLabel control={<Checkbox checked={notifyEmail} onChange={e => setNotifyEmail(e.target.checked)} disabled={isLoading} />} label='Receive Email Notifications' />
          <FormControlLabel control={<Checkbox checked={notifyPhone} onChange={e => setNotifyPhone(e.target.checked)} disabled={isLoading} />} label='Receive Text Notifications' />
          <FormControlLabel control={<Checkbox disabled checked={notifyPush} onChange={e => setNotifyPush(e.target.checked)} />} label='Receive Push Notifications' />
        </FormGroup>

        <Typography variant='h2' style={{ marginTop: '1em' }}>Do you agree to be notified of new engagements through the methods selected above?*</Typography>
        <Typography variant='subtitle' style={{ textTransform: 'uppercase', width: '100%', marginBottom: '0.5em', marginTop: '0.5em' }}>We will not use your contact information for any marketing purposes.</Typography>
        <FormGroup row style={{ width: '100%' }}>
          <FormControlLabel control={<Checkbox checked={agreeToTerms} disabled={isLoading} onChange={e => setAgreeToTerms(e.target.checked)} />} label='Yes' />
          <FormControlLabel control={<Checkbox checked={!agreeToTerms} disabled={isLoading} onChange={e => setAgreeToTerms(!e.target.checked)} />} label='No' />
        </FormGroup>

        {/* Identity Info */}
        <Typography variant='h2' style={{ marginTop: '1em' }}>Optional Identity Info</Typography>
        <Typography variant='body1'>This can be modified at any time from your profile.</Typography>
        <IdentityForm
          disabled={isLoading}
          demographics={demographics}
          onChange={(value) => setDemographics(value)}
          campaign={campaign}
        />

        <Button fullWidth type='submit' isLoading={isLoading} style={{ marginTop: '1em' }}>Submit</Button>
        <Button fullWidth secondary onClick={() => setPageIndex(1)} disabled={isLoading} style={{ marginTop: '10px' }}>Back</Button>
      </form>}
    </Container>
  );
}
export default CreateAccount;